/**
 * VoicemailFab — floating action button that shows unread badge
 * and opens the voicemail inbox overlay.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentConference } from '../../../base/conference/functions';
import { loadInbox, loadUnreadCount } from '../../actions';
import { getPersistentJson } from '../../../vrs-auth/storage';
const FAB_STYLE = {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 52,
    height: 52,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #1565c0, #0d47a1)',
    border: 'none',
    color: '#fff',
    fontSize: 22,
    cursor: 'pointer',
    zIndex: 9000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease'
};
const BADGE_STYLE = {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    background: '#e53935',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '20px',
    textAlign: 'center',
    padding: '0 5px',
    border: '2px solid #fff'
};
const PHONE_DOWN_ICON = (React.createElement("svg", { fill: 'none', height: '24', stroke: 'currentColor', strokeWidth: 2, viewBox: '0 0 24 24', width: '24' },
    React.createElement("path", { d: 'M16 3h5v5', strokeLinecap: 'round', strokeLinejoin: 'round' }),
    React.createElement("path", { d: 'M21 3l-7 7', strokeLinecap: 'round', strokeLinejoin: 'round' }),
    React.createElement("path", { d: 'M5.5 12.5c2.5 4.9 6.1 8 10 8 1 0 1.8-.8 1.8-1.8v-2.1c0-.8-.5-1.5-1.2-1.8l-2.4-.8c-.7-.2-1.5 0-1.9.6l-.8 1c-1.8-.8-3.2-2.2-4-4l1-.8c.6-.5.8-1.3.6-1.9l-.8-2.4c-.3-.7-1-1.2-1.8-1.2H3.9C2.9 4.7 2.1 5.5 2.1 6.5c0 2 .7 4 3.4 6z', strokeLinecap: 'round', strokeLinejoin: 'round' })));
function hasClientVoicemailAuth() {
    const auth = getPersistentJson('vrs_auth_token');
    const user = getPersistentJson('vrs_user_info');
    return Boolean(auth?.token && user?.role === 'client');
}
const VoicemailFab = () => {
    const [isAuthenticatedClient, setAuthenticatedClient] = useState(hasClientVoicemailAuth);
    const conference = useSelector(getCurrentConference);
    const room = useSelector((state) => state['features/base/conference'].room);
    const unreadCount = useSelector((state) => state['features/voicemail'].unreadCount);
    const inboxOpen = useSelector((state) => state['features/voicemail'].inboxOpen);
    const dispatch = useDispatch();
    useEffect(() => {
        const refreshAuthState = () => setAuthenticatedClient(hasClientVoicemailAuth());
        window.addEventListener('storage', refreshAuthState);
        window.addEventListener('focus', refreshAuthState);
        return () => {
            window.removeEventListener('storage', refreshAuthState);
            window.removeEventListener('focus', refreshAuthState);
        };
    }, []);
    useEffect(() => {
        if (isAuthenticatedClient) {
            dispatch(loadUnreadCount());
        }
    }, [dispatch, isAuthenticatedClient]);
    const handleClick = useCallback(() => {
        if (!inboxOpen) {
            dispatch(loadInbox());
        }
        dispatch({
            type: inboxOpen ? 'VOICEMAIL_CLOSE_INBOX' : 'VOICEMAIL_OPEN_INBOX'
        });
    }, [dispatch, inboxOpen]);
    // The global shortcut must not cover the conference toolbar, especially
    // its More control on narrow screens, from the moment the page is a room
    // (the join takes seconds). The inbox remains available at home.
    if (!isAuthenticatedClient || conference || room) {
        return null;
    }
    return (React.createElement("button", { onClick: handleClick, style: {
            ...FAB_STYLE,
            transform: unreadCount > 0 ? 'scale(1)' : 'scale(0.85)',
            opacity: unreadCount > 0 ? 1 : 0.6
        }, title: `Voicemail${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`, type: 'button' },
        PHONE_DOWN_ICON,
        unreadCount > 0 && (React.createElement("span", { style: BADGE_STYLE }, unreadCount > 99 ? '99+' : unreadCount))));
};
export default VoicemailFab;
